/**
 * Hito (feedback Carlos §89): «qué ha pasado» en el vocabulario del catálogo.
 * Lo usan Cambios y el Radar. Reglas:
 *   - El COLOR va por GRUPO de hito (identifica el tema, igual que los colores
 *     por tipo de cambio que Luis quiso conservar).
 *   - La SEVERIDAD A/D/I va en un chip NEUTRO: es urgencia de acción, NO signo
 *     de la noticia. Nunca un semáforo (R-18).
 *   - Sin hito mapeado → `fallback` (en Cambios, el badge de tipo de siempre).
 */
import type { ReactNode } from "react";
import { groupColor, useShowHitoCode } from "../lib/hitos";

export type HitoLike = {
  hito_id: string | null; hito_name: string | null;
  hito_group: number | null; severity: "A" | "D" | "I" | null;
  sentido?: "favorable" | "adverso" | "neutro" | null;      // R-19: para quien mira (titularidad)
};

/** Sentido (R-19): ▲ favorable · ▼ adverso. Neutro y mercado no llevan glifo.
 *  Es un atributo distinto de la severidad: aquí sí hay signo, calculado por hito × titularidad. */
export function SentidoGlyph({ sentido, t }: { sentido: HitoLike["sentido"]; t: (k: string) => string }) {
  if (sentido !== "favorable" && sentido !== "adverso") return null;
  const col = sentido === "favorable" ? "#16803f" : "#b4364b";
  return (
    <span title={t(`hitos.sentido_${sentido}`)} aria-label={t(`hitos.sentido_${sentido}`)}
      style={{ color: col, fontSize: "0.7rem", lineHeight: 1, flexShrink: 0 }}>
      {sentido === "favorable" ? "▲" : "▼"}
    </span>
  );
}

export function SeverityChip({ severity, t }: { severity: "A" | "D" | "I"; t: (k: string) => string }) {
  // La letra sola (A/D/I) no se entendía sin leyenda — feedback Luis, 9 sep.
  // Se escribe la palabra («Acción», «Decisión», «Info») en gris, sin caja:
  // sigue siendo urgencia de acción, nunca un semáforo (R-18).
  return (
    <span title={t(`hitos.sev${severity}`)}
      style={{ color:"var(--clr-text4)", fontSize:"0.66rem", fontWeight:600, letterSpacing:0.2, whiteSpace:"nowrap", lineHeight:1.3, flexShrink:0 }}>
      {t(`hitos.sev${severity}Short`)}
    </span>
  );
}

/** `showCode`: en tablas densas (Cambios) la pastilla lleva el CÓDIGO del hito
 *  («PR-01»), no el nombre, que se llevaba medio ancho de la tabla. El nombre
 *  completo queda en el tooltip. Radar y Calendario siguen con el nombre. */
export function HitoBadge({ c, t, fallback = null, maxWidth = 280, title, showCode = false, extra = null, showSeverity = true }: {
  c: HitoLike; t: (k: string) => string; fallback?: ReactNode; maxWidth?: number; title?: string;
  showCode?: boolean;
  /** Se pinta justo detrás del hito, antes de la severidad. */
  extra?: ReactNode;
  /** En Cambios la severidad tiene columna propia, así que aquí se oculta. */
  showSeverity?: boolean;
}) {
  const codeVisible = useShowHitoCode();
  if (!c.hito_id) return <>{fallback}</>;
  // Fuera del superadmin no se enseña el código, ni en la pastilla ni en el tooltip.
  showCode = showCode && codeVisible;
  const col = groupColor(c.hito_group);
  // El title va en el contenedor, no solo sobre el texto: así el tooltip sale
  // pasando por cualquier punto de la celda (feedback Luis, 9 sep).
  const tip = title ?? (codeVisible ? `${c.hito_id} · ${c.hito_name ?? ""}` : (c.hito_name ?? ""));
  return (
    <span title={tip} style={{ display:"inline-flex", alignItems:"center", gap:8, maxWidth, minWidth:0 }}>
      <span style={showCode
        ? { background: col+"1c", color: col, padding:"2px 7px", borderRadius:999, fontSize:"0.7rem", fontWeight:700,
            letterSpacing:0.3, whiteSpace:"nowrap", fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace" }
        : { background: col+"1c", color: col, padding:"2px 9px", borderRadius:999, fontSize:"0.72rem", fontWeight:600,
            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
        {showCode ? c.hito_id : (c.hito_name ?? (codeVisible ? c.hito_id : "—"))}
      </span>
      {extra}
      {showSeverity && c.severity && <SeverityChip severity={c.severity} t={t} />}
      <SentidoGlyph sentido={c.sentido} t={t} />
    </span>
  );
}

/** Señales de Carlos (28 sep) sobre agrupaciones homogéneas: «primer competidor
 *  en tu agrupación» y «tu CN queda por encima del precio menor». Ámbar y con
 *  texto: son avisos para actuar, no un semáforo de severidad. */
export function ImpactFlags({ c, t }: {
  c: { first_competitor?: boolean; above_precio_menor?: boolean | null; historico?: boolean };
  t: (k: string) => string;
}) {
  const chip = (label: string, hint: string, col: string) => (
    <span key={label} title={hint}
      style={{ display:"inline-block", marginTop:3, marginRight:4, color:col, border:`1px solid ${col}55`, background:`${col}14`,
               borderRadius:999, padding:"1px 7px", fontSize:"0.64rem", fontWeight:700, whiteSpace:"nowrap" }}>
      {label}
    </span>
  );
  const out = [];
  if (c.first_competitor) out.push(chip(`⚑ ${t("hitos.firstCompetitor")}`, t("hitos.firstCompetitorHint"), "#b45309"));
  if (c.above_precio_menor) out.push(chip(t("hitos.abovePM"), t("hitos.abovePMHint"), "#b45309"));
  if (c.historico) out.push(chip(t("hitos.historico"), t("hitos.historicoHint"), "#64748b"));
  return out.length ? <div>{out}</div> : null;
}

/** Aviso general (Carlos, 28 sep): «HelixScan monitoriza eventos desde el …». */
export function MonitoringNote({ since, t, fmtDate }: {
  since: string | null | undefined; t: (k: string, p?: Record<string, string | number>) => string;
  fmtDate: (d: Date) => string;
}) {
  if (!since) return null;
  return (
    <div style={{ fontSize:"0.72rem", color:"var(--clr-text4)", margin:"2px 0 10px" }}>
      {t("hitos.monitoringSince", { date: fmtDate(new Date(since)) })}
    </div>
  );
}

/** Chip de grupo de hitos (11 grupos, color propio). Sin hito → «Sin clasificar». */
export function GroupChip({ grupo, t }: { grupo: number | null; t: (k: string) => string }) {
  if (grupo == null) return <span style={{ fontSize:"0.72rem", color:"var(--clr-text4)" }}>{t("hitos.noHito")}</span>;
  const col = groupColor(grupo);
  return (
    <span title={t(`hitos.group${grupo}`)} style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:"0.72rem", fontWeight:600, color:"var(--clr-text2)", whiteSpace:"nowrap" }}>
      <span aria-hidden style={{ width:8, height:8, borderRadius:999, background:col, flexShrink:0 }} />
      {t(`hitos.groupShort${grupo}`)}
    </span>
  );
}
